import React, { useEffect, useState } from "react";
import {
  Card,
  Checkbox,
  Form,
  Input,
  Button,
  Spin,
  notification,
  Pagination,
  Typography,
} from "antd";
import { useParams } from "react-router-dom";

import {
  getBettingWebsiteByID,
  pushIntoBettingWebsiteQANDA,
  pullFromBettingWebsiteQANDA,
} from "../action";
import { renderMultilangText } from "../../../lib/functions";
import { IBettingWebsiteQAndA } from "../../../types/BettingWebsite.QAndA";
import { getQuestions } from "../../Questions/action";

const { Title } = Typography;

type Props = {
  fetchData: () => void;
};

export const EditQAndAForm: React.FC<Props> = (props) => {
  const { id } = useParams();
  const [allQuestions, setAllQuestions] = useState<IBettingWebsiteQAndA[]>([]);
  const [initialSelected, setInitialSelected] = useState<
    Record<string, string>
  >({});
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [api, contextHolder] = notification.useNotification();

  // Pagination & Search state
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const [search, setSearch] = useState("");

  const fetchData = async () => {
    if (!id) return;
    setLoading(true);
    try {
      // Fetch all questions (you can update this to be paginated API if supported)
      const questions = await getQuestions(1000, 0, "");
      setAllQuestions(questions);

      const website = await getBettingWebsiteByID(id);
      const selectedQAs = website?.QAndA || [];

      const selectedMap: Record<string, boolean> = {};
      const answersMap: Record<string, string> = {};

      selectedQAs.forEach((qa) => {
        selectedMap[qa.Question.id] = true;
        answersMap[qa.Question.id] = qa.Answer;
      });

      setInitialSelected(answersMap);
      setSelected(selectedMap);
      setAnswers(answersMap);
    } catch {
      api.error({ message: "Failed to fetch Q&A data." });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [id]);

  // Filter questions by search
  const filteredQuestions = allQuestions.filter((q) =>
    renderMultilangText(q.Question).toLowerCase().includes(search.toLowerCase())
  );

  // Paginate filtered questions
  const paginatedQuestions = filteredQuestions.slice(
    (page - 1) * pageSize,
    page * pageSize
  );

  const handleCheckboxChange = (questionId: string, checked: boolean) => {
    setSelected({ ...selected, [questionId]: checked });
  };

  const handleAnswerChange = (questionId: string, value: string) => {
    setAnswers({ ...answers, [questionId]: value });
  };

  const handleSave = async () => {
    if (!id) return;
    setSaving(true);

    try {
      for (const question of allQuestions) {
        const isSelected = !!selected[question.id];
        const wasSelected = initialSelected.hasOwnProperty(question.id);
        const answer = answers[question.id] || "";

        if (isSelected && !wasSelected) {
          await pushIntoBettingWebsiteQANDA(id, question.id, answer);
        } else if (!isSelected && wasSelected) {
          await pullFromBettingWebsiteQANDA(
            id,
            question.id,
            initialSelected[question.id]
          );
        } else if (
          isSelected &&
          wasSelected &&
          answer !== initialSelected[question.id]
        ) {
          await pushIntoBettingWebsiteQANDA(id, question.id, answer);
        }
      }

      api.success({ message: "Q&A updated successfully." });
      await fetchData();
    } catch {
      api.error({ message: "Failed to save changes." });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card title="Edit Questions and Answers">
      {contextHolder}
      {loading ? (
        <Spin />
      ) : (
        <>
          <Input.Search
            placeholder="Search questions..."
            allowClear
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1); // reset page when search changes
            }}
            style={{ marginBottom: 16 }}
          />
          <Form layout="vertical" className="space-y-3">
            {paginatedQuestions.length === 0 && <div>No questions found.</div>}
            {paginatedQuestions.map((q) => (
              <div
                className="p-2 border rounded flex flex-row items-center justify-between"
                key={q.id}
              >
                <Checkbox
                  checked={!!selected[q.id]}
                  onChange={(e) => handleCheckboxChange(q.id, e.target.checked)}
                >
                  <strong>{renderMultilangText(q.Question)}</strong>
                </Checkbox>
                {selected[q.id] && (
                  <Form.Item className="my-auto" style={{ marginBottom: 0 }}>
                    {q.Type === "B" ? (
                      <Checkbox
                        checked={answers[q.id] === "YES"}
                        onChange={(e) =>
                          handleAnswerChange(
                            q.id,
                            e.target.checked ? "YES" : "NO"
                          )
                        }
                      >
                        {answers[q.id] === "YES" ? "YES" : "NO"}
                      </Checkbox>
                    ) : (
                      <Input
                        value={answers[q.id] || ""}
                        onChange={(e) =>
                          handleAnswerChange(q.id, e.target.value)
                        }
                        placeholder="Write your answer..."
                      />
                    )}
                  </Form.Item>
                )}
              </div>
            ))}
          </Form>

          <Pagination
            current={page}
            pageSize={pageSize}
            total={filteredQuestions.length}
            onChange={(p) => setPage(p)}
            style={{ marginTop: 16, textAlign: "right" }}
            showSizeChanger={false}
          />

          <Button
            type="primary"
            onClick={handleSave}
            loading={saving}
            disabled={loading}
            style={{ marginTop: 16 }}
          >
            Save Changes
          </Button>
        </>
      )}
    </Card>
  );
};
